import React, { useState, useEffect, useRef } from "react";
import { sizes } from "./sizes";

function PrintOrderScreen({ selectedPhotos = [], onBack, startIndex = 0, quantities, setQuantities, photoOverlays, setPhotoOverlays = () => { }, croppedImages = {}, setCroppedImages = () => { }, }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageResolution, setImageResolution] = useState(null);
  const imgRef = useRef(null);
  const [imgBox, setImgBox] = useState({ width: 0, height: 0 });
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const photoOverlaysRef = useRef(photoOverlays);

  // FIX 1: Inject 'category' into allSizes so lookups (via .find) have the category data
  const allSizes = sizes.flatMap((cat) =>
    cat.items.map((item) => ({ ...item, category: cat.category }))
  );

  const startResize = useRef(null);
  const [activeCategory, setActiveCategory] = useState("");
  const sizesContainerRef = useRef(null);
  const categoryRefs = useRef({});
  const categoryScrollRef = useRef(null);
  const resizeState = useRef(null);
  const PRINT_SCALE = 80; // px per inch

  const getAspectRatioForPrint = (size) => {
    if (!size) return 1;
    const category = size.category;

    switch (category) {
      case "Square Lustre Prints":
        return 1; // 1:1 ALWAYS

      case "Panoramic":
        // Panoramic logic: generally Height / Width
        return size.heightIn / size.widthIn;

      case "Lustre Rectangular Prints":
      case "Glossy Rectangular Prints":
      case "Enlargements":
      default:
        return size.widthIn / size.heightIn;
    }
  };

  const getRealFrame = (overlay) => {
    const box = getRenderedImageBox();
    if (!overlay?.frame || !box.width || !box.height) return overlay?.frame;

    const nf = overlay.normalizedFrame;
    const isMobile = typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia("(max-width: 640px)").matches;

    if (!isMobile) {
      return nf
        ? {
          width: nf.width * box.width,
          height: nf.height * box.height,
          left: box.left + nf.left * box.width,
          top: box.top + nf.top * box.height,
        }
        : overlay.frame;
    }

    const frame = nf
      ? {
        width: nf.width * box.width,
        height: nf.height * box.height,
        left: box.left + nf.left * box.width,
        top: box.top + nf.top * box.height,
      }
      : overlay.frame;

    // Recalculate the visible frame against the current rendered image. This
    // prevents a frame saved at desktop dimensions from extending outside the
    // image after the preview is resized for a phone or tablet.
    const aspect = frame.height ? frame.width / frame.height : 1;
    return clampFrameToImage(
      clampFrameSizeToImage(frame, box, aspect, overlay.rotation || 0),
      box,
      overlay.rotation || 0
    );
  };

  useEffect(() => {
    const container = sizesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const blocks = container.querySelectorAll(".size-category-block");

      let current = "";
      blocks.forEach((block) => {
        const rect = block.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // distance of category block from scroll container top
        const top = rect.top - containerRect.top;

        // Perfect threshold for your sticky bars
        if (top <= 140 && top > -rect.height + 140) {
          current = block.getAttribute("data-category");
        }
      });

      if (current && current !== activeCategory) {
        setActiveCategory(current);

        // Auto-scroll horizontal category bar
        const tab = document.querySelector(`.cat-tab[data-cat="${current}"]`);
        if (tab && categoryScrollRef.current) {
          tab.scrollIntoView({ behavior: "smooth", inline: "center" });
        }
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [activeCategory]);

  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const overlay = getOverlayForCurrent();
    const current = selectedPhotos[currentIndex];
    if (!overlay?.frame || !current?.selectedSize) return;

    // This lookup works now because allSizes has category injected
    const size = allSizes.find(s => s.label === current.selectedSize);
    if (!size) return;

    resizeState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startFrame: { ...(getRealFrame(overlay) || overlay.frame) },
      aspect: getAspectRatioForPrint(size)
    };

    if (e.currentTarget?.setPointerCapture && e.pointerId !== undefined) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }

    window.addEventListener("pointermove", handleResizing);
    window.addEventListener("pointerup", handleResizeEnd);
    window.addEventListener("pointercancel", handleResizeEnd);
  };

  const handleResizing = (e) => {
    if (!resizeState.current) return;

    const { startX, startY, startFrame, aspect } = resizeState.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    const scaleX = (startFrame.width + dx) / startFrame.width;
    const scaleY = (startFrame.height + dy) / startFrame.height;
    const scale = Math.max(scaleX, scaleY);
    const box = getRenderedImageBox();
    const overlay = getOverlayForCurrent();
    const rotation = overlay?.rotation || 0;
    const nextFrame = clampFrameToImage(
      clampFrameSizeToImage(
        {
          ...startFrame,
          width: startFrame.width * scale,
          height: (startFrame.width * scale) / aspect,
        },
        box,
        aspect,
        rotation
      ),
      box,
      rotation
    );

    updateOverlayForCurrent({
      frame: nextFrame,
    });
  };
  const handleResizeEnd = () => {
    resizeState.current = null;
    window.removeEventListener("pointermove", handleResizing);
    window.removeEventListener("pointerup", handleResizeEnd);
    window.removeEventListener("pointercancel", handleResizeEnd);
  };

  const getFilterStyle = (filter) => {
    switch (filter) {
      case "blackwhite":
        return "grayscale(100%)";
      case "sepia":
        return "sepia(100%)";
      default:
        return "none";
    }
  };

  // ---------- Helpers ----------
  const getRenderedImageBox = () => {
    if (!imgRef.current) return { width: 0, height: 0, top: 0, left: 0 };

    const img = imgRef.current;
    const container = img.parentElement;
    // 🔥 USE offset values (NOT getBoundingClientRect)
    const width = img.offsetWidth;
    const height = img.offsetHeight;

    // Centered image inside flex container
    // Use the image's actual layout offset. On mobile the preview navigation
    // buttons and responsive flex sizing can make the image's horizontal
    // offset differ from the simple centered-container calculation.
    const isMobile = typeof window !== "undefined" && typeof window.matchMedia === "function"
      && window.matchMedia("(max-width: 640px)").matches;
    const left = isMobile
      ? img.offsetLeft
      : (container.clientWidth - width) / 2;
    const top = isMobile
      ? img.offsetTop
      : (container.clientHeight - height) / 2;

    return {
      width,
      height,
      left,
      top
    };
  };

  const computeOverlayFrame = (size, box, mode) => {
    if (!size || !box || box.width <= 0 || box.height <= 0)
      return { width: 0, height: 0 };
    const aspectRatio = getAspectRatioForPrint(size);


    let frameWidth = box.width;
    let frameHeight = frameWidth / aspectRatio;

    if (mode === "contain") {
      if (frameHeight > box.height) {
        frameHeight = box.height;
        frameWidth = frameHeight * aspectRatio;
      }
    } else if (mode === "cover") {
      frameHeight = box.height;
      frameWidth = frameHeight * aspectRatio;
      if (frameWidth < box.width) {
        frameWidth = box.width;
        frameHeight = frameWidth / aspectRatio;
      }
    }
    return { width: frameWidth, height: frameHeight };
  };

  const normalizeRotation = (rotation = 0) => ((rotation % 360) + 360) % 360;

  const getFrameVisualMetrics = (frame, rotation = 0) => {
    const normalizedRotation = normalizeRotation(rotation);
    const isSideways = normalizedRotation === 90 || normalizedRotation === 270;

    return {
      width: isSideways ? frame.height : frame.width,
      height: isSideways ? frame.width : frame.height,
      offsetLeft: isSideways ? (frame.width - frame.height) / 2 : 0,
      offsetTop: isSideways ? (frame.height - frame.width) / 2 : 0,
    };
  };

  const clampFrameSizeToImage = (frame, box, aspect, rotation = 0) => {
    if (!box.width || !box.height || !aspect) return frame;

    const normalizedRotation = normalizeRotation(rotation);
    const isSideways = normalizedRotation === 90 || normalizedRotation === 270;
    const minWidth = Math.min(50, isSideways ? box.height : box.width);
    const maxWidth = isSideways
      ? Math.min(box.height, box.width * aspect)
      : Math.min(box.width, box.height * aspect);
    const safeMaxWidth = Math.max(1, maxWidth);
    const width = Math.min(Math.max(frame.width, Math.min(minWidth, safeMaxWidth)), safeMaxWidth);

    return {
      ...frame,
      width,
      height: width / aspect,
    };
  };

  const clampFrameToImage = (frame, box, rotation = 0) => {
    if (!box.width || !box.height) return frame;

    const visual = getFrameVisualMetrics(frame, rotation);
    const minLeft = box.left - visual.offsetLeft;
    const maxLeft = box.left + box.width - visual.width - visual.offsetLeft;
    const minTop = box.top - visual.offsetTop;
    const maxTop = box.top + box.height - visual.height - visual.offsetTop;

    return {
      ...frame,
      left: minLeft <= maxLeft
        ? Math.min(Math.max(frame.left, minLeft), maxLeft)
        : box.left + (box.width - frame.width) / 2,
      top: minTop <= maxTop
        ? Math.min(Math.max(frame.top, minTop), maxTop)
        : box.top + (box.height - frame.height) / 2,
    };
  };

  const getDpiFrame = (size) => {
    if (!size) return { width: 0, height: 0 };

    // 1 inch = 150 px at 150dpi (or 300 if you prefer)
    const requiredW = size.widthIn * 150;
    const requiredH = size.heightIn * 150;

    return {
      width: requiredW,
      height: requiredH
    };
  };

  const checkSizeFit = (size) => {
    if (!imageResolution) return true;
    const requiredW = size.widthIn * 150;
    const requiredH = size.heightIn * 150;
    return (
      imageResolution.width >= requiredW &&
      imageResolution.height >= requiredH
    );
  };

  useEffect(() => {
    setCurrentIndex(startIndex);
  }, [startIndex, selectedPhotos]);

  useEffect(() => {
    const current = selectedPhotos[currentIndex];
    if (current) {
      const img = new Image();
      img.src = current.url;
      img.onload = () =>
        setImageResolution({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => setImageResolution(null);
    } else {
      setImageResolution(null);
    }
  }, [selectedPhotos, currentIndex]);

  useEffect(() => {
    const updateBox = () => {
      if (imgRef.current) {
        const rect = imgRef.current.getBoundingClientRect();
        setImgBox({ width: rect.width, height: rect.height });
      }
    };
    updateBox();
    const observer = new ResizeObserver(updateBox);
    if (imgRef.current) observer.observe(imgRef.current);
    window.addEventListener("resize", updateBox);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateBox);
    };
  }, [currentIndex]);

  useEffect(() => {
    photoOverlaysRef.current = photoOverlays;
  }, [photoOverlays]);

  const getOverlayForCurrent = () => {
    const current = selectedPhotos[currentIndex];
    if (!current) return null;
    return photoOverlaysRef.current[current.name]?.[current.selectedSize] || null;
  };

  const updateOverlayForCurrent = (updates) => {
    const current = selectedPhotos[currentIndex];
    if (!current) return;

    const sizeLabel = current.selectedSize;
    if (!sizeLabel) return;

    const box = getRenderedImageBox();

    let normalizedFrame = updates.normalizedFrame;

    // 🔥 AUTO-GENERATE normalized frame if frame exists
    if (updates.frame && box.width && box.height) {
      const f = updates.frame;

      normalizedFrame = {
        left: (f.left - box.left) / box.width,
        top: (f.top - box.top) / box.height,
        width: f.width / box.width,
        height: f.height / box.height,
      };
    }

    setPhotoOverlays(prev => {
      const next = {
        ...prev,
        [current.name]: {
          ...prev[current.name],
          [sizeLabel]: {
            ...prev[current.name]?.[sizeLabel],
            ...updates,
            ...(normalizedFrame && { normalizedFrame }),
          }
        }
      };
      photoOverlaysRef.current = next;
      return next;
    });
  };

  const updateQuantity = (sizeLabel, delta) => {
    const current = selectedPhotos[currentIndex];
    if (!current) return;

    current.selectedSize = sizeLabel;


    // compute new per-photo quantities based on current quantities prop
    const currentQtys = quantities[current.name] || {};
    const newQtyForPhoto = {
      ...currentQtys,
      [sizeLabel]: Math.max(0, (currentQtys[sizeLabel] || 0) + delta),
    };

    // create the new global quantities object
    const newQuantities = {
      ...quantities,
      [current.name]: newQtyForPhoto,
    };

    // update global quantities
    setQuantities(newQuantities);

    // If ALL sizes for this photo are now zero → reset cropped image and overlay
    // 🔥 REMOVE overlay for THIS size if qty is now zero
    if (newQtyForPhoto[sizeLabel] === 0) {
      setPhotoOverlays(prev => {
        const copy = { ...prev };
        if (copy[current.name]?.[sizeLabel]) {
          delete copy[current.name][sizeLabel];
        }
        // clean empty photo key
        if (Object.keys(copy[current.name] || {}).length === 0) {
          delete copy[current.name];
        }
        return copy;
      });
    }

    // 🔥 If ALL sizes are zero → full cleanup (keep your existing logic)
    const allZeros = Object.values(newQtyForPhoto).every(q => q === 0);
    if (allZeros) {
      setCroppedImages(prev => {
        const copy = { ...prev };
        delete copy[current.name];
        return copy;
      });
    }
    else {
      // If not all zeros, we still want to keep overlay/size info updated like before
      const s = allSizes.find((x) => x.label === sizeLabel);
      if (!s) return;
      ensureOverlayForSize(s)
      const box = getRenderedImageBox();

      // use DPI scaling
      const f = getDpiFrame(s, box, imageResolution);
    }
  };

  const resetTransformations = () => {
    const current = selectedPhotos[currentIndex];
    if (!current?.selectedSize) return;

    const overlay = getOverlayForCurrent();
    if (!overlay) return;

    updateOverlayForCurrent({
      fitMode: "crop",
      hasShrinkApplied: false,
      rotation: 0,
      scale: 1,
      frame: overlay.initialFrame, // restore first-time frame
    });
  };

  const _performCropAndSave = (photo, sizeLabel, overlayInfo) => {
    const img = imgRef.current;
    if (!overlayInfo || !img || !sizeLabel) return;

    const sizeObj = allSizes.find(s => s.label === sizeLabel);
    if (!sizeObj) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const rotation = overlayInfo.rotation || 0;
    const box = getRenderedImageBox();
    const frame = getRealFrame(overlayInfo) || overlayInfo.frame;

    const scaleX = img.naturalWidth / box.width;
    const scaleY = img.naturalHeight / box.height;

    // ✅ STEP 1: Convert frame → image space
    const relLeft = frame.left - box.left;
    const relTop = frame.top - box.top;

    let cropX = relLeft * scaleX;
    let cropY = relTop * scaleY;
    let cropW = frame.width * scaleX;
    let cropH = frame.height * scaleY;

    const isShrinkMode = overlayInfo.fitMode === "contain" && overlayInfo.hasShrinkApplied;
    const normalizedRotation = ((rotation % 360) + 360) % 360;

    // Clamp crop to image bounds
    cropX = Math.max(0, Math.min(cropX, img.naturalWidth - 1));
    cropY = Math.max(0, Math.min(cropY, img.naturalHeight - 1));
    cropW = Math.max(1, Math.min(cropW, img.naturalWidth - cropX));
    cropH = Math.max(1, Math.min(cropH, img.naturalHeight - cropY));

    // ✅ STEP 3: Setup canvas
    canvas.width = Math.max(1, Math.round(cropW));
    canvas.height = Math.max(1, Math.round(cropH));

    // White background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (isShrinkMode) {
      // Shrink-to-fit ON: keep angle-wise rotation behavior.
      const radians = (rotation * Math.PI) / 180;
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(radians);
      ctx.drawImage(
        img,
        cropX,
        cropY,
        cropW,
        cropH,
        -canvas.width / 2,
        -canvas.height / 2,
        canvas.width,
        canvas.height
      );
      ctx.restore();
    } else {
      // Shrink-to-fit OFF: orientation-only crop (horizontal/vertical).
      const isVertical = normalizedRotation === 90 || normalizedRotation === 270;
      if (isVertical) {
        const centerX = cropX + cropW / 2;
        const centerY = cropY + cropH / 2;
        const swappedW = cropH;
        const swappedH = cropW;
        cropW = swappedW;
        cropH = swappedH;
        cropX = centerX - cropW / 2;
        cropY = centerY - cropH / 2;
      }

      cropX = Math.max(0, Math.min(cropX, img.naturalWidth - 1));
      cropY = Math.max(0, Math.min(cropY, img.naturalHeight - 1));
      cropW = Math.max(1, Math.min(cropW, img.naturalWidth - cropX));
      cropH = Math.max(1, Math.min(cropH, img.naturalHeight - cropY));

      canvas.width = Math.max(1, Math.round(cropW));
      canvas.height = Math.max(1, Math.round(cropH));
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, canvas.width, canvas.height);
    }

    const croppedURL = canvas.toDataURL("image/jpeg", 0.95);
    const uniqueKey = `${photo.name}_${sizeLabel}`;

    setCroppedImages(prev => ({
      ...prev,
      [uniqueKey]: croppedURL
    }));
  };

  const cropCurrentImage = () => {
    const overlay = getOverlayForCurrent();
    const current = selectedPhotos[currentIndex];

    // Safety check: ensure we have an overlay, an image, and a selected size
    if (!overlay?.frame || !imgRef.current || !current?.selectedSize) return;

    // Call the reusable helper function
    _performCropAndSave(current, current.selectedSize, overlay);
  };

  const handleDone = () => {
    const currentPhoto = selectedPhotos[currentIndex];
    if (!currentPhoto) {
      onBack();
      return;
    }

    // 1. Get all size labels that have a quantity > 0 for the current photo
    const selectedSizeLabels = Object.keys(quantities[currentPhoto.name] || {})
      .filter(label => (quantities[currentPhoto.name][label] || 0) > 0);

    // 2. Loop through all ordered sizes for this photo and save the crop
    selectedSizeLabels.forEach(sizeLabel => {
      // Retrieve the size-specific overlay frame from photoOverlays state
      // Assumes photoOverlays structure: { [photoName]: { [sizeLabel]: { frame: {...} } } }
      const overlay = photoOverlaysRef.current[currentPhoto.name]?.[sizeLabel];

      // 3. Perform the crop if the specific overlay frame exists
      if (overlay) {
        _performCropAndSave(currentPhoto, sizeLabel, overlay);
      }
    });

    // Go back to Review screen after processing all crops
    onBack();
  };

  const handlePrev = () => {
    const len = selectedPhotos.length;
    if (len === 0) return;

    // Crop only if a size is selected
    const hasSelectedSize = Object.values(quantities[selectedPhotos[currentIndex].name] || {}).some(q => q > 0);
    if (hasSelectedSize) {
      cropCurrentImage();
    }

    // Always move to previous photo
    setCurrentIndex((prev) => (prev - 1 + len) % len);
  };

  const handleNext = () => {
    const len = selectedPhotos.length;
    if (len === 0) return;

    // Crop only if a size is selected
    const hasSelectedSize = Object.values(quantities[selectedPhotos[currentIndex].name] || {}).some(q => q > 0);
    if (hasSelectedSize) {
      cropCurrentImage();
    }

    // Always move to next photo
    setCurrentIndex((prev) => (prev + 1) % len);
  };

  const handleCenter = () => {
    const current = selectedPhotos[currentIndex];
    if (!current) return;

    const sizeLabel = current.selectedSize;
    if (!sizeLabel) return;

    const box = getRenderedImageBox();
    if (!box.width || !box.height) return;

    const overlay = getOverlayForCurrent();
    const padding = 35;

    // Calculate the actual frame size (existing or new fit)
    const frameWidth = overlay?.frame?.width
      ? overlay.frame.width
      : box.width - padding * 2;

    const frameHeight = overlay?.frame?.height
      ? overlay.frame.height
      : box.height - padding * 2;

    // CENTER the frame inside the image
    const centeredLeft = box.left + (box.width - frameWidth) / 2;
    const centeredTop = box.top + (box.height - frameHeight) / 2;

    // Normalized frame for exporting/cropping
    const normalizedFrame = {
      left: (centeredLeft - box.left) / box.width,
      top: (centeredTop - box.top) / box.height,
      width: frameWidth / box.width,
      height: frameHeight / box.height,
    };

    updateOverlayForCurrent({
      frame: {
        width: frameWidth,
        height: frameHeight,
        left: centeredLeft,
        top: centeredTop,
      },
      initialFrame: {
        width: frameWidth,
        height: frameHeight,
        left: centeredLeft,
        top: centeredTop,
      },
      rotation: 0,
      fitMode: "manual",
      scale: 1,
      extraPadding: padding,
      normalizedFrame,
      baseImageBox: box,
    });
  };

  const handleShrinkToFit = () => {
    const current = selectedPhotos[currentIndex];
    if (!current?.selectedSize) return;

    const overlay = getOverlayForCurrent();
    if (!overlay?.frame || overlay.hasShrinkApplied) return;

    const selectedSize = allSizes.find(s => s.label === current.selectedSize);
    if (!selectedSize) return;

    const box = getRenderedImageBox();
    if (!box.width || !box.height) return;

    const printAspect = getAspectRatioForPrint(selectedSize);
    const isPanoramic = selectedSize.category === "Panoramic";

    let frameWidth, frameHeight;

    if (isPanoramic) {
      // Width-first fit for panoramic
      frameWidth = box.width;
      frameHeight = frameWidth / printAspect;

      // If taller than container, clamp to height
      if (frameHeight > box.height) {
        frameHeight = box.height;
        frameWidth = frameHeight * printAspect;
      }
    } else {
      // Standard fit logic
      const imageAspect = box.width / box.height;
      if (imageAspect > printAspect) {
        frameHeight = box.height;
        frameWidth = frameHeight * printAspect;
      } else {
        frameWidth = box.width;
        frameHeight = frameWidth / printAspect;
      }
    }

    const left = box.left + (box.width - frameWidth) / 2;
    const top = box.top + (box.height - frameHeight) / 2;

    updateOverlayForCurrent({
      fitMode: "contain",
      hasShrinkApplied: true,
      frame: { width: frameWidth, height: frameHeight, left, top },
    });
  };

  const handleRotate = () => {
    const current = selectedPhotos[currentIndex];
    if (!current?.selectedSize) return;

    const overlay = getOverlayForCurrent();
    if (!overlay) return;

    const normalizedRotation = normalizeRotation(overlay.rotation || 0);
    const isShrinkMode = overlay.fitMode === "contain" && overlay.hasShrinkApplied;
    const nextRotation = isShrinkMode
      ? (normalizedRotation + 90) % 360
      : ((normalizedRotation === 90 || normalizedRotation === 270) ? 0 : 90);

    if (isShrinkMode) {
      updateOverlayForCurrent({
        rotation: nextRotation
      });
      return;
    }

    const selectedSize = allSizes.find(s => s.label === current.selectedSize);
    const box = getRenderedImageBox();
    const frame = selectedSize && overlay.frame
      ? clampFrameToImage(
        clampFrameSizeToImage(overlay.frame, box, getAspectRatioForPrint(selectedSize), nextRotation),
        box,
        nextRotation
      )
      : overlay.frame;

    updateOverlayForCurrent({
      rotation: nextRotation,
      frame,
    });
  };
  const startDrag = useRef(null);
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const overlay = getOverlayForCurrent();
    if (!overlay?.frame) return;
    startDrag.current = {
      x: e.clientX,
      y: e.clientY,
      frame: { ...(getRealFrame(overlay) || overlay.frame) },
    };
    if (e.currentTarget?.setPointerCapture && e.pointerId !== undefined) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }

    window.addEventListener("pointermove", handleMouseMove);
    window.addEventListener("pointerup", handleMouseUp);
    window.addEventListener("pointercancel", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!startDrag.current) return;

    const dx = e.clientX - startDrag.current.x;
    const dy = e.clientY - startDrag.current.y;
    const box = getRenderedImageBox();
    const overlay = getOverlayForCurrent();
    if (!overlay?.frame) return;

    const nextFrame = clampFrameToImage(
      {
        ...overlay.frame,
        left: startDrag.current.frame.left + dx,
        top: startDrag.current.frame.top + dy,
      },
      box,
      overlay.rotation || 0
    );

    updateOverlayForCurrent({
      frame: nextFrame,
    });
  };
  const handleMouseUp = () => {
    startDrag.current = null;
    window.removeEventListener("pointermove", handleMouseMove);
    window.removeEventListener("pointerup", handleMouseUp);
    window.removeEventListener("pointercancel", handleMouseUp);
  };

  const handleToggleallSizes = () => {
    if (!showSelectedOnly) {
      // User wants to switch to "Show Selected allSizes"
      const selectedallSizes = allSizes.filter(
        (s) => (quantities[currentPhoto.name]?.[s.label] || 0) > 0
      );

      if (selectedallSizes.length === 0) {
        // 🚨 No allSizes selected → redirect back to all allSizes list
        setShowSelectedOnly(false); // make sure it's on full list
        return;
      }
    }

    // ✅ Toggle normally if at least one size is selected
    setShowSelectedOnly(!showSelectedOnly);
  };

  const currentPhoto = selectedPhotos[currentIndex];
  const currentOverlay = getOverlayForCurrent();
  if (!currentPhoto) {
    return (<div className="print-order-container">
      <div style={{ padding: 24 }}>No photo selected.</div> </div>
    );
  }

  const allSizesToRender = showSelectedOnly
    ? allSizes.filter((s) => (quantities[currentPhoto.name]?.[s.label] || 0) > 0)
    : allSizes;

  const ensureOverlayForSize = (size) => {
    const current = selectedPhotos[currentIndex];
    if (!current) return;

    const sizeLabel = size.label;
    if (photoOverlaysRef.current[current.name]?.[sizeLabel]) return; // already exists

    const box = getRenderedImageBox();
    if (!box.width || !box.height) return;

    const frame = getCenteredOverlayFrame(size, box);

    setPhotoOverlays(prev => {
      const next = {
        ...prev,
        [current.name]: {
          ...prev[current.name],
          [sizeLabel]: {
            fitMode: "crop",
            rotation: 0,
            scale: 1,
            frame,
            initialFrame: frame,
          },
        },
      };
      photoOverlaysRef.current = next;
      return next;
    });
  };

  const handleSelectSize = (size) => {
    const current = selectedPhotos[currentIndex];
    if (!current) return;

    current.selectedSize = size.label;

    const existingOverlay = photoOverlaysRef.current[current.name]?.[size.label];
    if (existingOverlay) {
      updateOverlayForCurrent({ ...existingOverlay });
      return;
    }

    // Ensure category is passed correctly
    const fullSize = { ...size, category: size.category || size.category };

    const box = getRenderedImageBox();
    if (!box.width || !box.height) return;

    // Use width-first for Panoramic
    const frame = getCenteredOverlayFrame(fullSize, box);

    setPhotoOverlays(prev => {
      const next = {
        ...prev,
        [current.name]: {
          ...prev[current.name],
          [size.label]: {
            fitMode: "crop",
            rotation: 0,
            scale: 1,
            frame,
            initialFrame: frame,
          },
        },
      };
      photoOverlaysRef.current = next;
      return next;
    });
  };

  const getPrintFrameFromSize = (size) => {
    return {
      width: size.widthIn * PRINT_SCALE,
      height: size.heightIn * PRINT_SCALE,
    };
  };

  const getCenteredOverlayFrame = (size, imageBox) => {
    const aspect = getAspectRatioForPrint(size);
    const isPanoramic = size.category === "Panoramic";

    let frameWidth, frameHeight;

    if (isPanoramic) {
      // Panoramic: Initialize to FULL WIDTH
      frameWidth = imageBox.width;
      frameHeight = frameWidth / aspect;

      // If it's too tall, scale down to fit height
      if (frameHeight > imageBox.height) {
        frameHeight = imageBox.height;
        frameWidth = frameHeight * aspect;
      }
    } else {
      // Standard: 80% width logic
      frameWidth = imageBox.width * 0.8;
      frameHeight = frameWidth / aspect;
      if (frameHeight > imageBox.height * 0.8) {
        frameHeight = imageBox.height * 0.8;
        frameWidth = frameHeight * aspect;
      }
    }

    return {
      width: frameWidth,
      height: frameHeight,
      left: imageBox.left + (imageBox.width - frameWidth) / 2,
      top: imageBox.top + (imageBox.height - frameHeight) / 2,
    };
  };
  const hasSelectedSize =
    Object.values(quantities[currentPhoto.name] || {}).some(q => q > 0);

  const isShrinkToFit =
    currentOverlay?.fitMode === "contain" &&
    currentOverlay?.hasShrinkApplied === true &&
    hasSelectedSize;

  const getShrinkPreviewStyle = () => {
    if (!isShrinkToFit || !currentOverlay?.frame || !imgRef.current) {
      return {};
    }

    const img = imgRef.current;
    const frame = currentOverlay.frame;

    const imgAspect = img.naturalWidth / img.naturalHeight;
    const frameAspect = frame.width / frame.height;

    let scale = 1;

    if (imgAspect > frameAspect) {
      scale = frame.width / img.clientWidth;
    } else {
      scale = frame.height / img.clientHeight;
    }

    return {
      transform: `scale(${scale})`,
      transformOrigin: "center center",
    };
  };
  const realFrame = getRealFrame(currentOverlay);
  const renderedImageBox = getRenderedImageBox();
  const relativeFrame = realFrame && renderedImageBox.width && renderedImageBox.height
    ? {
      ...realFrame,
      left: realFrame.left - renderedImageBox.left,
      top: realFrame.top - renderedImageBox.top,
    }
    : null;
  const useMobileCropViewport = typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(max-width: 640px)").matches;
  const displayFrame = useMobileCropViewport ? relativeFrame : realFrame;
  // Mobile can recalculate/clamp a saved desktop frame to the current image.
  // Keep the resize handle attached to that displayed frame. Desktop keeps
  // its original frame dimensions and positioning.
  const resizeHandleFrame = useMobileCropViewport && displayFrame
    ? displayFrame
    : currentOverlay?.frame;
  const mobileResizeHandleStyle = useMobileCropViewport && resizeHandleFrame
    ? (() => {
      const rotation = normalizeRotation(currentOverlay?.rotation || 0);
      const width = resizeHandleFrame.width;
      const height = resizeHandleFrame.height;
      return {
        top: rotation === 90 || rotation === 180
          ? "-2px"
          : undefined,
        bottom: rotation === 0 || rotation === 270
          ? "-2px"
          : undefined,
        left: rotation === 90 || rotation === 180
          ? (rotation === 90 ? width - 38 : "-2px")
          : undefined,
        right: rotation === 0 || rotation === 90
          ? "-2px"
          : (rotation === 180 || rotation === 270 ? width - 38 : undefined),
        ...(rotation === 180 ? { bottom: height - 38 } : {}),
        ...(rotation === 270 ? { left: "-2px" } : {}),
      };
    })()
    : {};
  const shouldShowCropOverlay = currentOverlay?.frame
    && (hasSelectedSize || (useMobileCropViewport && currentPhoto.selectedSize));
  return (
    <div className="print-order-container"> <div className="print-order-content">
      {/* LEFT SIDE */}
      <div className="preview-column">
        <div className="print-order-topbar">
          <div className="photo-info">
            <span className="photo-index">{currentIndex + 1} of {selectedPhotos.length} photos</span>
            <span className="photo-name">{currentPhoto.name}</span>
          </div>
          <button className="print-done-btn" onClick={handleDone}>
            <i className="fa-solid fa-check"></i> Done
          </button>
        </div>

        <div className="preview-image-wrapper">
          {/* ⬅️ Prev Button (outside image left side) */}
          {selectedPhotos.length > 1 && (
            <button className="nav-outside left" onClick={handlePrev}>
              <i className="fa-solid fa-chevron-left"></i>
            </button>
          )}

          {/* 🖼️ Image Container */}
          <div
            className="image-container"
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              overflow: "hidden",
              backgroundColor: hasSelectedSize ? "#5d5d5d" : "#2E2E2E",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              ref={imgRef}
              key={currentPhoto.name}
              src={currentPhoto.url}
              alt={currentPhoto.name}
              className="print-preview"
              style={{
                objectFit: "contain",
                objectPosition: "center",
                width: "60%",
                height: "auto",
                display: "block",
                filter: getFilterStyle(currentPhoto.filterApplied),
                // Shrink ON -> angle-wise image rotation. Shrink OFF -> image stays unrotated.
                transform: `
                  ${isShrinkToFit ? `rotate(${currentOverlay?.rotation || 0}deg)` : ""}
                  ${isShrinkToFit ? getShrinkPreviewStyle().transform || "" : ""}
                `,
                transformOrigin: "center center",
              }}
              draggable={false}
            />

            {/* 🟩 Crop Overlay */}
            {shouldShowCropOverlay && displayFrame && (
                <div
                  className="crop-viewport"
                  style={{
                    position: "absolute",
                    top: useMobileCropViewport ? renderedImageBox.top : 0,
                    left: useMobileCropViewport ? renderedImageBox.left : 0,
                    width: useMobileCropViewport ? renderedImageBox.width : 0,
                    height: useMobileCropViewport ? renderedImageBox.height : 0,
                    overflow: "hidden", // ensure image stays inside overlay
                    clipPath: useMobileCropViewport ? "inset(0)" : undefined,
                    pointerEvents: "none",
                  }}
                >
                  <div
                    className="crop-overlay"
                    style={{
                      position: "absolute",
                      top: displayFrame.top,
                      left: displayFrame.left,
                      width: displayFrame.width,
                      height: displayFrame.height,
                      border: "4px solid #2962FF",
                      boxSizing: "border-box",
                      transform: `rotate(${currentOverlay.rotation || 0}deg)`,
                      transformOrigin: "center center",
                      cursor: "move",
                      overflow: "hidden", // ensure image stays inside overlay
                      backgroundColor: isShrinkToFit ? "#ffffff" : "transparent", // empty space white
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      pointerEvents: "auto",
                    }}
                    onPointerDown={handleMouseDown}
                  >
                  {/* Image centered inside overlay */}
                  {isShrinkToFit && imgRef.current && (
                    <img
                      src={currentPhoto.url}
                      alt={currentPhoto.name}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        display: "block",
                        filter: getFilterStyle(currentPhoto.filterApplied),
                      }}
                      draggable={false}
                    />
                  )}

                  {/* Resize Handle */}
                  {!isShrinkToFit && (
                    <div
                      className="resize-handle"
                      style={{
                        position: "absolute",
                        width: "40px",
                        height: "40px",
                        background: "#2962FF",
                        borderRadius: "3px",
                        cursor: "nwse-resize",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "#fff",
                        fontSize: "12px",
                        pointerEvents: "all",

                        // -----------------------------
                        // Angle-wise positioning logic
                        // -----------------------------

                        top: (() => {
                          const r = currentOverlay.rotation || 0;
                          if (r === 0) return undefined;                  // 0° → bottom-right (top not set)
                          if (r === 90) return "-2px";                    // 90° → top-right
                          if (r === 180) return "-2px";                   // 180° → top-left
                          if (r === 270) return currentOverlay.frame.height - 38; // 270° → bottom-left
                          return undefined;
                        })(),

                        bottom: (() => {
                          const r = currentOverlay.rotation || 0;
                          if (r === 0) return "-2px";                     // 0° → bottom-right
                          if (r === 90) return undefined;                // 90° → top-right (bottom not set)
                          if (r === 180) return currentOverlay.frame.height - 38; // 180° → top-left
                          if (r === 270) return "-2px";                  // 270° → bottom-left
                          return "-2px";
                        })(),

                        left: (() => {
                          const r = currentOverlay.rotation || 0;
                          if (r === 0) return undefined;                 // 0° → bottom-right (left not set)
                          if (r === 90) return currentOverlay.frame.width - 38;  // 90° → top-right
                          if (r === 180) return "-2px";                  // 180° → top-left
                          if (r === 270) return "-2px";                  // 270° → bottom-left
                          return undefined;
                        })(),

                        right: (() => {
                          const r = currentOverlay.rotation || 0;
                          if (r === 0) return "-2px";                    // 0° → bottom-right
                          if (r === 90) return "-2px";                   // 90° → top-right
                          if (r === 180) return currentOverlay.frame.width - 38; // 180° → top-left
                          if (r === 270) return currentOverlay.frame.width - 38; // 270° → bottom-left
                          return "-2px";
                        })(),

                        // Mobile uses the clamped, image-relative frame.
                        ...mobileResizeHandleStyle,

                        transform: `rotate(${-currentOverlay.rotation || 0}deg)`,
                        transformOrigin: "center center",
                      }}
                      onPointerDown={handleResizeStart}
                    >
                      <i className="fa-solid fa-crop"></i>
                    </div>
                  )}


                  </div>
                </div>
              )}

            {/* ⚠ Low Resolution Warning */}
            {allSizes.some(
              (s) =>
                (quantities[currentPhoto.name]?.[s.label] || 0) > 0 &&
                !checkSizeFit(s)
            ) && <div className="warning-overlay">⚠ Low Resolution</div>}
          </div>


          {/* ➡️ Next Button (outside image right side) */}
          {selectedPhotos.length > 1 && (
            <button className="nav-outside right" onClick={handleNext}>
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          )}
        </div>
        <div className="preview-controls">
          <button onClick={handleCenter}>Center</button> <button onClick={handleShrinkToFit}>Shrink to Fit</button>
          <button onClick={handleRotate}>Rotate</button> <button onClick={resetTransformations}>Reset</button>
        </div>
      </div>

      {/* RIGHT SIDE PANEL */}
      <div className="allSizes-column" ref={sizesContainerRef}>

        {/* HEADER */}
        <div className="sizes-top-title">
          <h3
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginLeft: "20px",
            }}
          >
            {showSelectedOnly ? (
              <>
                <i
                  className="fa-solid fa-arrow-left"
                  style={{ cursor: "pointer", fontSize: "16px", color: "#333" }}
                  title="Show all sizes"
                  onClick={handleToggleallSizes}
                ></i>
                <span style={{ marginLeft: "6px" }}>Selected Print Sizes</span>
              </>
            ) : (
              "Select Print Sizes"
            )}
          </h3>

          {Object.values(quantities[currentPhoto.name] || {}).some((q) => q > 0) && (
            <button
              className={`sizes-toggle-btn ${showSelectedOnly ? "active" : ""}`}
              onClick={handleToggleallSizes}
            >
              {showSelectedOnly ? "Show All Sizes" : "Show Selected Sizes"}
            </button>
          )}

          {/* HORIZONTAL CATEGORY BAR */}
          {!showSelectedOnly && (
            <div className="category-bar-wrapper">
              <button
                className="cat-scroll-btn left"
                onClick={() => {
                  categoryScrollRef.current.scrollBy({ left: -150, behavior: "smooth" });
                }}
              >
                <i className="fa-solid fa-chevron-left"></i>
              </button>

              <div className="category-horizontal-bar" ref={categoryScrollRef}>
                {sizes.map((cat) => (
                  <button
                    data-cat={cat.category}
                    key={cat.category}
                    className={`cat-tab ${activeCategory === cat.category ? "active" : ""}`}
                    onClick={() => {
                      const el = categoryRefs.current[cat.category];
                      if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }}
                  >
                    {cat.category}
                  </button>

                ))}
              </div>

              <button
                className="cat-scroll-btn right"
                onClick={() => {
                  categoryScrollRef.current.scrollBy({ left: 150, behavior: "smooth" });
                }}
              >
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>

        {/* ───────────────────────────────────
      BUILD CATEGORY LIST SAFELY  
      ─────────────────────────────────── */}
        {(() => {
          // 1. full category structure from sizes.js
          const allCategories = sizes;

          // 2. build filtered structure safely
          const structured = showSelectedOnly
            ? allCategories
              .map((cat) => ({
                ...cat,
                items: (cat.items || []).filter(
                  (s) => (quantities[currentPhoto.name]?.[s.label] || 0) > 0
                ),
              }))
              .filter((cat) => cat.items.length > 0)
            : allCategories;

          // 3. handle empty case
          if (!structured.length) {
            return (
              <p style={{ fontStyle: "italic", color: "#555" }}>
                No sizes selected yet.
              </p>
            );
          }

          // 4. final safe mapping
          return structured.map((cat) => (
            <div key={cat.category}
              className="size-category-block"
              data-category={cat.category}
              ref={(el) => (categoryRefs.current[cat.category] = el)}>

              {/* CATEGORY BUTTON / HEADER */}
              <h4 className="size-category-btn">{cat.category}</h4>

              {/* CATEGORY ITEMS */}
              {(cat.items || []).map((s) => {
                const qty = quantities[currentPhoto.name]?.[s.label] || 0;
                const ok = checkSizeFit(s);

                return (
                  <div
                    key={s.label}
                    className="size-row"
                    // FIX 2: Pass the category explicitly to handleSelectSize
                    onClick={() => handleSelectSize({ ...s, category: cat.category })}
                    style={{
                      backgroundColor: qty > 0 ? "#2962FF" : "transparent",
                      color: qty > 0 ? "white" : "inherit",
                      borderRadius: "6px",
                      padding: "8px 12px",
                      marginBottom: "6px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transition: "0.2s ease",
                    }}
                  >
                    <span>
                      {s.label} – ${s.price.toFixed(2)} each{" "}
                      {!ok && (
                        <i
                          className="fa-solid fa-triangle-exclamation"
                          style={{
                            color: "yellow",
                            textShadow: "0 0 2px black",
                            marginLeft: "8px",
                          }}
                        ></i>
                      )}
                    </span>

                    <div className="qty-controls">
                      <button
                        className={qty > 0 ? "active" : ""}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateQuantity(s.label, -1);
                        }}
                      >
                        -
                      </button>
                      <span>{qty}</span>
                      <button
                        className={qty > 0 ? "active" : ""}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateQuantity(s.label, 1);
                        }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ));
        })()}
      </div>

    </div>
    </div>

  );
}

export default PrintOrderScreen;
